import { useMemo, useCallback } from 'react';
import { Box, Flex, Grid, Icon, PlainButton, SkeletonShape } from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { useCopilotSessionContext } from '@/features/copilot';
import { open as openSidebar } from '@/features/sidebar';
import { useAuth } from '@/services/auth';
import { useServicesQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { useSetupGuide } from '@/features/setup-guide';
import { getPromptsForWorkspace, type PromptCard as PromptCardData } from '../prompts';
import { buildWorkspaceContext } from '../workspace-context';
import { personalizeCards } from '../personalize-cards';
import { SectionLabel } from './section-label';
import * as styles from './prompt-cards.css';

const queryOptions = { staleTime: 5 * 60_000 } as const;

export function PromptCards({ workspaceId }: { workspaceId: number }) {
  const auth = useAuth();
  const enabled = workspaceId > 0;

  const { data: environmentData } = useServicesQuery(
    'getEnvironment',
    { params: { environmentId: workspaceId } },
    { ...queryOptions, enabled },
  );

  const { setupGuideQuery } = useSetupGuide();
  const setupGuideData = setupGuideQuery.data;

  const { data: campaignsData, isError: campaignsError } = useServicesQuery(
    'getCampaigns',
    { params: { environment_id: workspaceId }, query: { page: 0, size: 1 } },
    { ...queryOptions, enabled },
  );

  const { data: segmentsData, isError: segmentsError } = useServicesQuery(
    'getSegments',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: newslettersData, isError: newslettersError } = useServicesQuery(
    'getNewsletters',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: transactionalData, isError: transactionalError } = useServicesQuery(
    'getTransactionalMessages',
    { params: { environment_id: workspaceId }, query: {} },
    { ...queryOptions, enabled },
  );

  const { data: eventsTotalsData, isError: eventsError } = useServicesQuery(
    'getEventNamesTotals',
    { params: { environment_id: workspaceId } },
    { ...queryOptions, enabled },
  );

  const { data: attributesTotalsData, isError: attributesError } = useServicesQuery(
    'getAttributesTotals',
    { params: { environment_id: workspaceId } },
    { ...queryOptions, enabled },
  );

  // Each count query has settled once it either succeeded or errored.
  // When all have settled but counts is still null, we know counts will never
  // arrive (partial or total failure) and should fall back to heuristic cards.
  // When queries are disabled (workspaceId <= 0), treat as settled immediately
  // so we fall back to heuristic cards instead of showing an infinite skeleton.
  const countsSettled =
    !enabled ||
    ((campaignsData != null || campaignsError) &&
      (segmentsData != null || segmentsError) &&
      (newslettersData != null || newslettersError) &&
      (transactionalData != null || transactionalError) &&
      (eventsTotalsData != null || eventsError) &&
      (attributesTotalsData != null || attributesError));

  const workspaceContext = useMemo(
    () =>
      buildWorkspaceContext({
        account: auth.account,
        environmentData: environmentData ?? null,
        setupGuide: setupGuideData?.setup_guide ?? null,
        campaignsData: campaignsData ?? null,
        segmentsData: segmentsData ?? null,
        newslettersData: newslettersData ?? null,
        transactionalData: transactionalData ?? null,
        eventsTotalsData: eventsTotalsData ?? null,
        attributesTotalsData: attributesTotalsData ?? null,
      }),
    [
      auth.account,
      environmentData,
      setupGuideData,
      campaignsData,
      segmentsData,
      newslettersData,
      transactionalData,
      eventsTotalsData,
      attributesTotalsData,
    ],
  );

  // Get a larger pool of heuristic-matched cards for the LLM to rank from
  const candidateCards = useMemo(() => getPromptsForWorkspace(workspaceContext, 8), [workspaceContext]);

  // Heuristic fallback — used if personalization fails or is still loading
  const heuristicCards = useMemo(() => candidateCards.slice(0, 4), [candidateCards]);

  // Stable key — changes when the candidate pool or counts change
  const candidateKey = useMemo(() => candidateCards.map((card) => card.title).join('|'), [candidateCards]);
  const countsReady = workspaceContext.counts != null;

  const { data: personalizedCards, isFetched: personalizationSettled } = useQuery({
    queryKey: ['personalizePromptCards', candidateKey, countsReady],
    queryFn: () =>
      personalizeCards({
        cards: candidateCards,
        workspaceContext,
      }),
    enabled: candidateCards.length > 0 && countsReady,
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Show skeleton until personalization completes (isFetched covers both success
  // and null returns) or until we know counts will never fully arrive (settled but incomplete)
  const countsFailed = countsSettled && !countsReady;
  const showSkeleton = !personalizationSettled && !countsFailed;
  const displayCards =
    personalizedCards ??
    heuristicCards.map((card) => ({
      card,
      title: card.title,
      description: card.description,
    }));

  if (displayCards.length === 0) {
    return null;
  }

  return (
    <Box w="full">
      <Box mb="150">
        <SectionLabel>What can the agent help with?</SectionLabel>
      </Box>
      <Grid columns="1fr 1fr" gap="150" w="full">
        {showSkeleton
          ? Array.from({ length: 4 }, (_, index) => <PromptCardSkeleton key={index} />)
          : displayCards.map((item) => (
              <PromptCard
                key={item.card.title}
                card={item.card}
                displayTitle={item.title}
                displayDescription={item.description}
              />
            ))}
      </Grid>
    </Box>
  );
}

function PromptCardSkeleton() {
  return (
    <div className={styles.card}>
      <Flex alignItems="center" gap="050">
        <SkeletonShape style={{ width: 14, height: 14 }} />
        <SkeletonShape style={{ width: '70%', height: 16 }} />
      </Flex>
      <Box mt="100">
        <SkeletonShape style={{ width: '90%', height: 14 }} />
      </Box>
    </div>
  );
}

function PromptCard({
  card,
  displayTitle,
  displayDescription,
}: {
  card: PromptCardData;
  displayTitle: string;
  displayDescription: string;
}) {
  const session = useCopilotSessionContext();
  const { track } = useAnalytics();

  const handleClick = useCallback(async () => {
    try {
      track('aiAssistant', 'promptCardClicked', {
        Title: card.title,
      });

      const newSession = session.reset();
      openSidebar('copilot');

      await newSession.sendMessage(card.prompt);
    } catch (error) {
      sentryCaptureException(error);
    }
  }, [session, card, track]);

  return (
    <GradientRing radius={4} display="flex" h="full">
      <PlainButton className={clsx(styles.card, gradientHoverTarget)} onClick={handleClick}>
        <Flex alignItems="center" gap="050">
          <Icon name="sparkle" size="xs" color="subtle" />
          <div className={styles.cardTitle}>{displayTitle}</div>
        </Flex>
        <div className={styles.cardDescription}>{displayDescription}</div>
      </PlainButton>
    </GradientRing>
  );
}
